import React, { useState } from 'react';
import ToolLayout from '../../components/ToolLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { Copy, Palette } from 'lucide-react';

const ColorPickerPage = () => {
  const { toast } = useToast();
  const [color, setColor] = useState('#3b82f6');

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const hexToHsl = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const rgb = hexToRgb(color);
  const hsl = hexToHsl(color);

  const copyValue = (value) => {
    navigator.clipboard.writeText(value);
    toast({
      title: 'Copied!',
      description: `${value} copied to clipboard`,
    });
  };

  return (
    <ToolLayout
      title="Color Picker"
      description="Pick colors and get HEX, RGB, and HSL values instantly. Perfect for designers and developers."
      icon={Palette}
    >
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 space-y-4">
            <div>
              <Label htmlFor="colorPicker">Select Color</Label>
              <div className="flex gap-3 mt-2">
                <input
                  id="colorPicker"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-20 h-20 rounded-lg cursor-pointer border-2 border-gray-200 dark:border-gray-700"
                />
                <div className="flex-1">
                  <Input
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="#3b82f6"
                    className="font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">HEX</p>
                      <p className="font-mono text-lg font-semibold">{color.toUpperCase()}</p>
                    </div>
                    <Button onClick={() => copyValue(color)} variant="ghost" size="sm">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {rgb && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">RGB</p>
                        <p className="font-mono text-lg font-semibold">rgb({rgb.r}, {rgb.g}, {rgb.b})</p>
                      </div>
                      <Button onClick={() => copyValue(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`)} variant="ghost" size="sm">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {hsl && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">HSL</p>
                        <p className="font-mono text-lg font-semibold">hsl({hsl.h}, {hsl.s}%, {hsl.l}%)</p>
                      </div>
                      <Button onClick={() => copyValue(`hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`)} variant="ghost" size="sm">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          <div className="flex-1">
            <Label>Color Preview</Label>
            <div
              className="mt-2 w-full h-64 rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-lg"
              style={{ backgroundColor: color }}
            ></div>
          </div>
        </div>
      </div>
    </ToolLayout>
  );
};

export default ColorPickerPage;